import React, { useState, useEffect } from 'react';
import { ordersAPI, Order, authAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../contexts/WebSocketContext';
import {
  TruckIcon,
  MapPinIcon,
  FunnelIcon,
  ClockIcon,
  XMarkIcon,
  BellIcon
} from '@heroicons/react/24/outline';

// Calculate distance between two coordinates using Haversine formula (in km)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Estimate time based on distance (rough estimate: 30 km/h average in city)
const estimateTime = (distanceKm: number): string => {
  const avgSpeedKmh = 30;
  const hours = distanceKm / avgSpeedKmh;
  const minutes = Math.round(hours * 60);
  
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};

interface JobWithDistance extends Order {
  distanceFromMe?: number;
  timeFromMe?: string;
}

const AvailableJobs: React.FC = () => {
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const [jobs, setJobs] = useState<JobWithDistance[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobWithDistance[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptingJobId, setAcceptingJobId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dispatcherLocation, setDispatcherLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [newJobAlert, setNewJobAlert] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    vehicleType: 'all',
    minPrice: '',
    maxPrice: '',
    packageType: 'all'
  });

  useEffect(() => {
    fetchDispatcherLocation();
  }, []);

  useEffect(() => {
    if (dispatcherLocation) {
      fetchAvailableJobs();
    } else {
      // Fetch jobs even without location, just won't show distance
      fetchAvailableJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatcherLocation]);

  useEffect(() => {
    const unsubscribeNewJob = subscribe('new_job', (message) => {
      console.log('🔔 New job available!', message);
      setNewJobAlert(true);
      
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('New Delivery Job Available!', {
          body: `₦${message.order?.final_price} - ${message.order?.package_type}`,
          icon: '/logo.png',
        });
      }
      
      setTimeout(() => setNewJobAlert(false), 5000);
      fetchAvailableJobs();
    });

    const unsubscribeJobAssigned = subscribe('job_assigned', (message) => {
      console.log('✅ Job assigned to you!', message);
      
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Job Assigned!', {
          body: message.message || 'You have been assigned a new delivery',
          icon: '/logo.png',
        });
      }
      
      fetchAvailableJobs();
    });

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      unsubscribeNewJob();
      unsubscribeJobAssigned();
    };
  }, [subscribe]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, filters]);

  const fetchDispatcherLocation = async () => {
    try {
      const profile = await authAPI.getProfile();
      if (profile.location) {
        setDispatcherLocation({
          lat: profile.location.latitude,
          lng: profile.location.longitude
        });
      }
    } catch (error) {
      console.error('Error fetching dispatcher location:', error);
    }
  };

  const fetchAvailableJobs = async () => {
    try {
      setLoading(true);
      const available = await ordersAPI.getAvailableJobs();
      
      // Calculate distance and time for each job if we have dispatcher location
      const jobsWithDistance: JobWithDistance[] = (available || []).map((job: Order) => {
        const pickupStop = (job as any).stops?.[0];
        if (dispatcherLocation && pickupStop?.latitude && pickupStop?.longitude) {
          const distance = calculateDistance(
            dispatcherLocation.lat,
            dispatcherLocation.lng,
            pickupStop.latitude,
            pickupStop.longitude
          );
          return {
            ...job,
            distanceFromMe: distance,
            timeFromMe: estimateTime(distance)
          };
        }
        return job;
      });
      
      setJobs(jobsWithDistance);
    } catch (error) {
      console.error('Error fetching available jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...jobs];

    // Vehicle type filter
    if (filters.vehicleType !== 'all') {
      filtered = filtered.filter((job: any) => 
        job.vehicle_type === filters.vehicleType
      );
    }

    // Price range filter
    if (filters.minPrice) {
      filtered = filtered.filter((job: any) => 
        (job.final_price || 0) >= parseFloat(filters.minPrice)
      );
    }
    if (filters.maxPrice) {
      filtered = filtered.filter((job: any) => 
        (job.final_price || 0) <= parseFloat(filters.maxPrice)
      );
    }

    // Package type filter
    if (filters.packageType !== 'all') {
      filtered = filtered.filter(job => 
        job.package_type === filters.packageType
      );
    }

    setFilteredJobs(filtered);
  };

  const handleAcceptJob = async (jobId: string) => {
    try {
      setAcceptingJobId(jobId);
      await ordersAPI.acceptOrder(jobId);
      // Navigate to My Jobs page after successful acceptance
      navigate('/my-jobs');
    } catch (error: any) {
      console.error('Error accepting job:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to accept job. Please try again.';
      
      // Show user-friendly error message
      if (errorMessage.includes('already have an active delivery')) {
        alert('⚠️ You can only accept one job at a time.\n\nPlease complete your current delivery before accepting a new job.\n\nGo to "My Jobs" to see your active delivery.');
      } else {
        alert(errorMessage);
      }
      setAcceptingJobId(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      vehicleType: 'all',
      minPrice: '',
      maxPrice: '',
      packageType: 'all'
    });
  };

  const vehicleTypes = [
    'all',
    'Bicycle',
    'Motorbike',
    'Sedan',
    'SUV',
    'Pickup Truck',
    'Medium Van',
    '12-Wheeler',
    '18-Wheeler',
    'Motorboat',
    'Light Plane',
    'Helicopter',
    'Cargo Plane'
  ];

  const packageTypes = ['all', 'document', 'package', 'fragile', 'bulk'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Available Jobs
          </h1>
          <p className="mt-2 text-gray-600">Browse and accept delivery jobs near you</p>
        </div>

        {/* Stats Bar */}
        <div className="bg-gradient-blue rounded-volexx shadow-volexx p-6 mb-6">
          <div className="flex items-center justify-between text-white">
            <div>
              <p className="text-sm opacity-90">Total Available</p>
              <p className="text-3xl font-bold">{filteredJobs.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Potential Earnings</p>
              <p className="text-3xl font-bold">
                ₦{filteredJobs.reduce((sum, job: any) => sum + (job.final_price || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-volexx hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="h-5 w-5 mr-2 text-gray-600" />
            <span className="font-medium text-gray-700">
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </span>
          </button>
          {(filters.vehicleType !== 'all' || filters.minPrice || filters.maxPrice || filters.packageType !== 'all') && (
            <button
              onClick={clearFilters}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <XMarkIcon className="h-5 w-5 mr-1" />
              Clear Filters
            </button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Vehicle Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Type
                </label>
                <select
                  value={filters.vehicleType}
                  onChange={(e) => setFilters({ ...filters, vehicleType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                >
                  {vehicleTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Vehicles' : type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Min Price Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Price ($)
                </label>
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>

              {/* Max Price Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Price ($)
                </label>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                  placeholder="999"
                  className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>

              {/* Package Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package Type
                </label>
                <select
                  value={filters.packageType}
                  onChange={(e) => setFilters({ ...filters, packageType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                >
                  {packageTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Jobs List */}
        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-volexx shadow-volexx p-12 text-center">
            <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs available</h3>
            <p className="text-gray-600">
              {jobs.length === 0 
                ? 'Check back soon for new delivery opportunities!' 
                : 'Try adjusting your filters to see more jobs.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-volexx shadow-volexx border border-gray-100 hover:shadow-lg transition-all duration-200">
                {/* Job Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-3 py-1 bg-volexx-blue-100 text-volexx-blue-800 text-sm font-semibold rounded-volexx">
                          {(job as any).vehicle_type || 'Any Vehicle'}
                        </span>
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-volexx">
                          ₦{Number((job as any).final_price ?? 0).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 capitalize">{job.package_type}</p>
                    </div>
                  </div>

                  {/* Distance and Time from Dispatcher */}
                  {job.distanceFromMe !== undefined && job.timeFromMe && (
                    <div className="mb-4 p-3 bg-gradient-to-r from-volexx-blue-50 to-volexx-orange-50 rounded-volexx border border-volexx-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <MapPinIcon className="h-5 w-5 text-volexx-blue-600 mr-2" />
                          <div>
                            <p className="text-xs text-gray-600 font-medium">Distance from you</p>
                            <p className="text-sm font-bold text-volexx-blue-700">
                              {job.distanceFromMe.toFixed(1)} km
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <ClockIcon className="h-5 w-5 text-volexx-orange-600 mr-2" />
                          <div>
                            <p className="text-xs text-gray-600 font-medium">Est. time</p>
                            <p className="text-sm font-bold text-volexx-orange-700">
                              {job.timeFromMe}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Locations */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start">
                      <div className="bg-volexx-blue-100 p-2 rounded-volexx">
                        <MapPinIcon className="h-4 w-4 text-volexx-blue-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-xs font-medium text-gray-500">Pickup</p>
                        <p className="text-sm font-medium text-gray-900">
                          {(job as any).stops?.[0]?.address || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-volexx-orange-100 p-2 rounded-volexx">
                        <MapPinIcon className="h-4 w-4 text-volexx-orange-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-xs font-medium text-gray-500">Dropoff</p>
                        <p className="text-sm font-medium text-gray-900">
                          {(job as any).stops?.[1]?.address || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Job Details */}
                  {(job as any).description && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-volexx">
                      <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                      <p className="text-sm text-gray-700">{(job as any).description}</p>
                    </div>
                  )}

                  {/* Accept Button */}
                  <button
                    onClick={() => handleAcceptJob(job.id)}
                    disabled={acceptingJobId === job.id}
                    className="w-full bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-3 rounded-volexx transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-poppins"
                  >
                    {acceptingJobId === job.id ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        <span>Accepting...</span>
                      </>
                    ) : (
                      <span>Accept Job</span>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableJobs;
